/**
 * Maps a UI phase plus elapsed time into calm, student-facing status text.
 * Pure on purpose: the component only supplies the clock. See FR-C14.
 */

export type Phase =
  | 'logging_in'
  | 'loading_exam'
  | 'saving'
  | 'submitting'
  | 'waiting'
  | 'retrying'
  | 'offline'

export type PhaseTone = 'neutral' | 'calm' | 'warn'

export interface PhaseInput {
  phase: Phase
  /** Whole seconds since the phase began. */
  elapsedSeconds: number
  /** Retry attempt number, 1-based; only used by `retrying`. */
  attempt?: number
  /** Seconds until the next retry; only used by `retrying`. */
  retryInSeconds?: number
}

export interface PhaseView {
  /** Main line. */
  text: string
  /** Reassurance line once the wait is long, else null. */
  reassurance: string | null
  /** Extra line about local safety once the wait is very long, else null. */
  safety: string | null
  /** "12 detik" style label, shown only when it adds information. */
  elapsedLabel: string | null
  tone: PhaseTone
}

export const SLOW_AFTER_SECONDS = 3
export const SAFE_AFTER_SECONDS = 8

const BASE_TEXT: Record<Phase, string> = {
  logging_in: 'Memeriksa data peserta…',
  loading_exam: 'Memuat soal…',
  saving: 'Menyimpan jawaban…',
  submitting: 'Mengirim jawaban…',
  waiting: 'Menunggu balasan server…',
  retrying: 'Server sedang ramai, mencoba lagi',
  offline: 'Tidak ada koneksi ke server.',
}

const SLOW_TEXT = 'Server sedang ramai. Mohon tunggu, jawaban Anda aman.'
const SAFE_TEXT = 'Jawaban tersimpan di perangkat ini dan akan dikirim ulang otomatis.'

/** Phases where answers already sit on the device, so the safety line is true. */
const KEEPS_LOCAL_COPY: ReadonlySet<Phase> = new Set(['saving', 'submitting', 'waiting', 'retrying', 'offline'])

export function describePhase(input: PhaseInput): PhaseView {
  const elapsed = Math.max(0, Math.floor(input.elapsedSeconds))
  let text = BASE_TEXT[input.phase]

  if (input.phase === 'retrying') {
    const attempt = Math.max(1, Math.floor(input.attempt ?? 1))
    const wait = Math.max(0, Math.ceil(input.retryInSeconds ?? 0))
    text = `Server sedang ramai, mencoba lagi (percobaan ${attempt}) dalam ${wait} detik`
  }

  if (input.phase === 'offline') {
    return { text, reassurance: null, safety: SAFE_TEXT, elapsedLabel: null, tone: 'warn' }
  }

  const slow = elapsed >= SLOW_AFTER_SECONDS || input.phase === 'retrying'
  const veryLong = elapsed >= SAFE_AFTER_SECONDS
  const safe = KEEPS_LOCAL_COPY.has(input.phase)

  return {
    text,
    reassurance: slow ? SLOW_TEXT : null,
    safety: veryLong && safe ? SAFE_TEXT : null,
    elapsedLabel: elapsed >= 1 ? `${elapsed} detik` : null,
    tone: input.phase === 'retrying' || veryLong ? 'warn' : slow ? 'calm' : 'neutral',
  }
}

/** Message shown after a 429; counts down in the UI. */
export function rateLimitMessage(seconds: number): string {
  const n = Math.max(0, Math.ceil(seconds))
  return `Terlalu banyak percobaan. Coba lagi dalam ${n} detik.`
}
